/**
 * OpenTelemetry Semantic Conventions
 *
 * Centralized attribute names for spans, logs, and metrics.
 * @see https://opentelemetry.io/docs/specs/semconv/
 */

/* eslint-disable @typescript-eslint/no-namespace */
export namespace OtelAttributes {
  /** Session tracking attributes */
  export namespace Session {
    export const ID = 'session.id' as const
    export const TEMP_ID = 'session.temp_id' as const
    export const STATUS = 'session.status' as const
    export const LOGIN_TIME = 'session.login_time' as const
    export const LOGOUT_TIME = 'session.logout_time' as const
    export const DURATION_MS = 'session.duration_ms' as const
  }

  /** Event classification attributes */
  export namespace Event {
    export const TYPE = 'event.type' as const
    export const SOURCE = 'event.source' as const
  }

  /** HTTP request/response attributes */
  export namespace HTTP {
    export const METHOD = 'http.method' as const
    export const URL = 'http.url' as const
    export const STATUS_CODE = 'http.status_code' as const
    export const REQUEST_ID = 'http.request_id' as const
  }

  /** Error and exception attributes */
  export namespace Error {
    export const MESSAGE = 'error.message' as const
    export const TYPE = 'error.type' as const
    export const STACK_TRACE = 'error.stack_trace' as const
  }

  /** Client metadata attributes */
  export namespace Client {
    export const IP = 'client.ip' as const
    export const USER_AGENT = 'client.user_agent' as const

    export namespace UserAgent {
      export const NAME = 'client.user_agent.name' as const
      export const VERSION = 'client.user_agent.version' as const

      export namespace OS {
        export const NAME = 'client.user_agent.os.name' as const
        export const VERSION = 'client.user_agent.os.version' as const
      }

      export const DEVICE = 'client.user_agent.device' as const
    }
  }

  /** Operation metrics attributes */
  export namespace Operation {
    export const NAME = 'operation.name' as const
    export const DURATION_MS = 'operation.duration_ms' as const
    export const OUTCOME = 'operation.outcome' as const
  }

  /** User identity attributes */
  export namespace User {
    export const ID = 'user.id' as const
    export const ROLE = 'user.role' as const
    export const TYPE = 'user.type' as const
    export const ROLES = 'user.roles' as const
    export const BRANCH = 'user.branch' as const
  }

  /** Common form attributes (shared across all forms) */
  export namespace Form {
    export const ID = 'form.id' as const
    export const TYPE = 'form.type' as const
    /** Traker id attributes */
    export const TRACKER_ID = 'tracker.id' as const
  }

  /** User interaction tracking attributes */
  export namespace Interaction {
    export const TYPE = 'interaction.type' as const
    export const PAGE = 'interaction.page' as const
    export const ELEMENT = 'interaction.element' as const
    export const TARGET_TAG = 'interaction.target_tag' as const
  }

  /** How the final message on the success page was resolved */
  export namespace SuccessResolution {
    export const SOURCE = 'success_resolution.source' as const
  }

  /** Service request attributes */
  export namespace ServiceRequest {
    export const NUMBER = 'service_request.number' as const
    export const TYPE = 'service_request.type' as const
    export const STATUS = 'service_request.status' as const
  }
}

/**
 * Creates a form-specific attributes namespace with automatic prefixing.
 *
 * Enforces naming convention: forms.{formId}.{attribute}
 *
 * @param formId - Form identifier (lowercase, e.g., 'fd03', 'cb01')
 * @param attributes - Attribute definitions with just the suffix
 * @returns Namespace with fully qualified attribute names
 *
 * @example
 * const FD03Attributes = createFormAttributes('fd03', {
 *   DEPOSIT_AMOUNT: 'deposit_amount',
 *   TENURE: 'tenure',
 * })
 * // Results in: FD03Attributes.DEPOSIT_AMOUNT = 'forms.fd03.deposit_amount'
 *
 * @example
 * // Nested namespaces
 * export namespace FD03Attributes {
 *   export const Validation = createFormAttributes('fd03.validation', {
 *     FIELD_NAME: 'field_name',
 *   })
 * }
 * // Results in: 'forms.fd03.validation.field_name'
 */
export function createFormAttributes<T extends Record<string, string>>(
  formId: string,
  attributes: T
): { readonly [K in keyof T]: string } {
  const result = {} as Record<string, string>

  for (const [key, suffix] of Object.entries(attributes)) {
    result[key] = `forms.${formId}.${suffix}`
  }

  return result as { readonly [K in keyof T]: string }
}
